/**
 * Load All Products for Apps/Solutions Pages
 * Loads remaining products beyond Shopify's 250 limit into DOM for pagination
 */
class LoadAllProducts {
  constructor() {
    this.init();
  }

  async init() {
    // Only run on apps/solutions pages
    if (!window.collectionData || !['apps', 'solutions'].includes(window.collectionData.handle)) {
      return;
    }

    const { handle, totalProducts, loadedProducts } = window.collectionData;
    
    if (loadedProducts >= totalProducts) {
      document.dispatchEvent(new CustomEvent('allProductsLoadedInDOM', {
        detail: { totalProducts: loadedProducts }
      }));
      return;
    }

    await this.loadRemainingProducts(handle, loadedProducts, totalProducts);
  }

  async loadRemainingProducts(handle, loadedProducts, totalProducts) {
    const grid = document.getElementById('product-grids');
    if (!grid) return;

    let page = 2; // Start from page 2 since page 1 is already loaded
    let currentLoaded = loadedProducts;

    while (currentLoaded < totalProducts) {
      try {
        // Use the products.json API to get actual product data
        const response = await fetch(`/collections/${handle}/products.json?page=${page}&limit=250`);
        if (!response.ok) break;

        const data = await response.json();
        const products = data.products || [];
        
        if (products.length === 0) break;

        // Create product cards from API data and append to grid
        products.forEach(product => {
          const productCard = this.createProductCardFromAPI(product);
          if (productCard) {
            grid.appendChild(productCard);
          }
        });

        currentLoaded += products.length;
        
        page++;
        
        // Break if we got less than 250 products (last page)
        if (products.length < 250) break;
        
      } catch (error) {
        // Silently handle errors to avoid console warnings
        break;
      }
    }

    // Trigger pagination recalculation
    document.dispatchEvent(new CustomEvent('allProductsLoadedInDOM', {
      detail: { totalProducts: currentLoaded }
    }));
  }

  createProductCardFromAPI(product) {
    try {
      const productCard = document.createElement('div');
      productCard.className = 'exchange-product-card';
      
      const imageUrl = product.images && product.images[0] ? product.images[0].src : '';
      const productUrl = `/products/${product.handle}`;
      
      // Extract metafield data for filters - ensure no undefined values
      const appType = this.extractMetafield(product, 'product_type_custom') || '';
      const category = this.extractListMetafield(product, 'multicat') || this.extractMetafield(product, 'categories') || '';
      const priceType = this.extractMetafield(product, 'price_types') || '';
      const saviyntProduct = this.extractListMetafield(product, 'savprod') || this.extractMetafield(product, 'saviynt_products') || '';
      const region = this.extractMetafield(product, 'region') || '';
      const industry = this.extractMetafield(product, 'industry') || '';
      
      const collectionHandle = window.collectionData?.handle || '';
      
      productCard.innerHTML = `
        <div class="card card-product text-start" 
             data-product-grid
             data-product-id="${product.id}"
             data-collections="${collectionHandle}"
             data-developer="${product.vendor || ''}"
             data-apptype="${appType}"
             data-category="${category}"
             data-pricetype="${priceType}"
             data-saviyntproduct="${saviyntProduct}"
             data-region="${region}"
             data-partner="${product.vendor || ''}"
             data-industry="${industry}"
             data-solutiontype="${appType}">
          <div class="product-image">
            <a href="${productUrl}" class="d-block product-link position-relative">
              ${imageUrl ? 
                `<img src="${imageUrl}" alt="${product.title}" loading="lazy" width="120" height="120">` :
                `<svg class="placeholder-svg" width="120" height="120" viewBox="0 0 120 120" fill="none">
                   <rect width="120" height="120" fill="#f8f9fa"/>
                   <text x="60" y="60" text-anchor="middle" fill="#6c757d" font-size="12">No Image</text>
                 </svg>`
              }
            </a>
          </div>
          <div class="card-body">
            <h2 class="card-product-title">${product.title}</h2>
            <div class="card-product-badge"></div>
            <div class="card-product-vendor">
              ${product.vendor ? `<p>By ${product.vendor}</p>` : ''}
            </div>
            <div class="card-product-desc">
              ${product.body_html ? `<p>${this.truncateText(this.stripHtml(product.body_html), 150)}</p>` : ''}
            </div>
            <div class="card-product-actions">
              <a href="${productUrl}" class="btn-view-details">View Details</a>
            </div>
          </div>
        </div>
      `;
      
      return productCard;
    } catch (error) {
      return null;
    }
  }

  stripHtml(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  extractListMetafield(product, fieldName) {
    if (product.metafields && product.metafields.custom && product.metafields.custom[fieldName]) {
      const value = product.metafields.custom[fieldName];
      if (Array.isArray(value)) return value.join(',');
      try { const parsed = JSON.parse(value); if (Array.isArray(parsed)) return parsed.join(','); } catch(e) {}
      return value || '';
    }
    return '';
  }

  extractMetafield(product, fieldName) {
    // Try to extract from metafields if available
    if (product.metafields && product.metafields.custom && product.metafields.custom[fieldName]) {
      const value = product.metafields.custom[fieldName];
      return value || '';
    }
    
    // Fallback to tags for metafield data
    if (product.tags && Array.isArray(product.tags)) {
      for (const tag of product.tags) {
        if (tag.toLowerCase().includes(fieldName.toLowerCase().replace('_', ' '))) {
          return tag || '';
        }
      }
    }
    
    return '';
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new LoadAllProducts();
});